import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  TrendingUp,
  Calendar,
  Download,
  Loader,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  PieChart
} from 'lucide-react';
import { financialAPI } from '../utils/api';

const Commission = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for commission data
  const [earnings, setEarnings] = useState({
    total_fees: 0,
    transaction_count: 0,
    total_volume: 0,
    onramp_fees: 0,
    offramp_fees: 0,
    onramp_count: 0,
    offramp_count: 0
  });

  const [dailyStats, setDailyStats] = useState([]);
  const [commissionSettings, setCommissionSettings] = useState({
    onramp_fee_percentage: 2,
    offramp_fee_percentage: 2,
    min_fee_ngn: 50,
    max_fee_ngn: 10000,
    referral_bonus_percentage: 1
  });

  // Period options
  const periods = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 3 months' },
    { value: 'all', label: 'All time' }
  ];

  // Fetch commission data on mount and when period changes
  useEffect(() => {
    fetchCommissionData();
  }, [selectedPeriod]);

  const fetchCommissionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate start_date based on selected period
      let start_date = null;
      if (selectedPeriod !== 'all') {
        const days = parseInt(selectedPeriod);
        start_date = new Date();
        start_date.setDate(start_date.getDate() - days);
        start_date = start_date.toISOString();
      }

      // Fetch data in parallel
      const [earningsRes, statsRes, settingsRes] = await Promise.all([
        financialAPI.getCommissionEarnings({ start_date, end_date: new Date().toISOString() }),
        financialAPI.getCommissionStats({ period: selectedPeriod }),
        financialAPI.getCommissionSettings()
      ]);

      // Process earnings data
      if (earningsRes.data.success) {
        const earningsData = earningsRes.data.data;
        const summary = earningsData.summary || {};
        const breakdown = earningsData.breakdown || [];

        // Find onramp and offramp breakdown
        const onrampBreakdown = breakdown.find(b => b._id === 'onramp') || {};
        const offrampBreakdown = breakdown.find(b => b._id === 'offramp') || {};

        setEarnings({
          total_fees: summary.total_fees || 0,
          transaction_count: summary.transaction_count || 0,
          total_volume: summary.total_volume || 0,
          onramp_fees: onrampBreakdown.total_fees || 0,
          offramp_fees: offrampBreakdown.total_fees || 0,
          onramp_count: onrampBreakdown.transaction_count || 0,
          offramp_count: offrampBreakdown.transaction_count || 0
        });
      }

      // Process daily stats
      if (statsRes.data.success) {
        const stats = statsRes.data.data || [];
        // Group by date and sum fees
        const dailyMap = {};
        stats.forEach(stat => {
          const date = stat._id?.date || 'Unknown';
          const rampType = stat._id?.ramp_type || 'unknown';

          if (!dailyMap[date]) {
            dailyMap[date] = {
              date,
              total_fees: 0,
              onramp_fees: 0,
              offramp_fees: 0,
              transaction_count: 0
            };
          }

          dailyMap[date].total_fees += stat.total_fees || 0;
          dailyMap[date].transaction_count += stat.transaction_count || 0;

          if (rampType === 'onramp') {
            dailyMap[date].onramp_fees += stat.total_fees || 0;
          } else if (rampType === 'offramp') {
            dailyMap[date].offramp_fees += stat.total_fees || 0;
          }
        });

        // Convert to array and sort by date (newest first)
        const dailyArray = Object.values(dailyMap).sort((a, b) =>
          new Date(b.date) - new Date(a.date)
        );

        setDailyStats(dailyArray);
      }

      // Process settings
      if (settingsRes.data.success) {
        setCommissionSettings(settingsRes.data.data || commissionSettings);
      }

    } catch (error) {
      console.error('Error fetching commission data:', error);
      setError('Failed to load commission data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatCurrencyNGN = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateAverageFee = () => {
    if (earnings.transaction_count === 0) return 0;
    return earnings.total_fees / earnings.transaction_count;
  };

  const calculateOnrampPercentage = () => {
    if (earnings.total_fees === 0) return 0;
    return ((earnings.onramp_fees / earnings.total_fees) * 100).toFixed(1);
  };

  const calculateOfframpPercentage = () => {
    if (earnings.total_fees === 0) return 0;
    return ((earnings.offramp_fees / earnings.total_fees) * 100).toFixed(1);
  };

  // Export report as CSV
  const exportReport = () => {
    const csvData = [
      ['Date', 'Total Fees', 'Onramp Fees', 'Offramp Fees', 'Transaction Count'],
      ...dailyStats.map(stat => [
        formatDate(stat.date),
        stat.total_fees.toFixed(2),
        stat.onramp_fees.toFixed(2),
        stat.offramp_fees.toFixed(2),
        stat.transaction_count
      ])
    ];

    const csv = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commission-report-${selectedPeriod}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Simple bar chart component
  const SimpleBarChart = ({ data }) => {
    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-48 text-vetme-gray-dark">
          No data available for selected period
        </div>
      );
    }

    const maxValue = Math.max(...data.map(d => d.total_fees || 0));

    return (
      <div className="space-y-4">
        <div className="flex items-end space-x-2 h-48">
          {data.map((item, index) => {
            const height = maxValue > 0 ? ((item.total_fees / maxValue) * 100) : 0;
            return (
              <div key={index} className="flex-1 flex flex-col items-center group">
                <div className="relative w-full">
                  {/* Tooltip */}
                  <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-vetme-dark text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                    {formatCurrency(item.total_fees)}
                  </div>

                  {/* Bar */}
                  <div
                    className="w-full bg-vetme-mint rounded-t transition-all duration-300 hover:bg-vetme-mint-dark"
                    style={{ height: `${height}%`, minHeight: height > 0 ? '4px' : '0' }}
                  />
                </div>
                <div className="text-xs text-vetme-gray-dark mt-2 text-center transform -rotate-45 origin-top-left">
                  {formatDate(item.date).split(',')[0]}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="w-12 h-12 text-vetme-mint-dark animate-spin mx-auto" />
          <p className="mt-4 text-vetme-gray-dark">Loading commission data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-medium">Error Loading Commission Data</h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button
                onClick={fetchCommissionData}
                className="mt-4 btn-vetme-primary"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="vetme-heading-big text-vetme-dark">Commission Report</h1>
          <p className="mt-2 text-vetme-gray-dark">
            Track commission earnings from onramp and offramp transactions (shown in USD).
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="input-vetme"
          >
            {periods.map(period => (
              <option key={period.value} value={period.value}>
                {period.label}
              </option>
            ))}
          </select>
          <button
            onClick={exportReport}
            className="btn-vetme-outline"
            disabled={dailyStats.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Commission Earned */}
        <div className="card-vetme p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-vetme-gray-dark">Total Commission</p>
              <p className="text-3xl font-bold text-vetme-dark mt-2">
                {formatCurrency(earnings.total_fees)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-vetme-gray-light">
            <div className="flex items-center text-sm">
              <span className="text-vetme-gray-dark">From {earnings.transaction_count} transactions</span>
            </div>
          </div>
        </div>

        {/* Onramp Commission */}
        <div className="card-vetme p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-vetme-gray-dark">Onramp Commission</p>
              <p className="text-3xl font-bold text-vetme-dark mt-2">
                {formatCurrency(earnings.onramp_fees)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <ArrowUpRight className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-vetme-gray-light">
            <div className="flex items-center justify-between text-sm">
              <span className="text-vetme-gray-dark">{earnings.onramp_count} transactions</span>
              <span className="font-medium text-blue-600">{calculateOnrampPercentage()}%</span>
            </div>
          </div>
        </div>

        {/* Offramp Commission */}
        <div className="card-vetme p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-vetme-gray-dark">Offramp Commission</p>
              <p className="text-3xl font-bold text-vetme-dark mt-2">
                {formatCurrency(earnings.offramp_fees)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
              <ArrowDownRight className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-vetme-gray-light">
            <div className="flex items-center justify-between text-sm">
              <span className="text-vetme-gray-dark">{earnings.offramp_count} transactions</span>
              <span className="font-medium text-orange-600">{calculateOfframpPercentage()}%</span>
            </div>
          </div>
        </div>

        {/* Average Fee */}
        <div className="card-vetme p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-vetme-gray-dark">Average Fee</p>
              <p className="text-3xl font-bold text-vetme-dark mt-2">
                {formatCurrency(calculateAverageFee())}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-vetme-gray-light">
            <div className="flex items-center text-sm">
              <span className="text-vetme-gray-dark">Per transaction</span>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Commission Chart */}
      <div className="card-vetme p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-vetme-dark">Daily Commission Breakdown</h3>
          <Calendar className="w-5 h-5 text-vetme-mint-dark" />
        </div>

        <SimpleBarChart data={dailyStats} />

        <div className="mt-6 pt-4 border-t border-vetme-gray-light">
          <div className="flex items-center justify-between">
            <div className="flex space-x-6">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-vetme-mint rounded-full mr-2"></div>
                <span className="text-sm text-vetme-gray-dark">Total Fees</span>
              </div>
            </div>
            <div className="text-sm text-vetme-gray-dark">
              Showing {dailyStats.length} days
            </div>
          </div>
        </div>
      </div>

      {/* Commission Breakdown Table */}
      <div className="card-vetme p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-vetme-dark">Detailed Breakdown</h3>
          <PieChart className="w-5 h-5 text-vetme-mint-dark" />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-vetme-gray-light">
            <thead className="bg-vetme-gray-lightest">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Total Fees
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Onramp Fees
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Offramp Fees
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Transactions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-vetme-gray-light">
              {dailyStats.length > 0 ? (
                dailyStats.map((stat, index) => (
                  <tr key={index} className="hover:bg-vetme-gray-lightest transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-vetme-dark">
                        {formatDate(stat.date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-vetme-dark">
                        {formatCurrency(stat.total_fees)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-blue-600">
                        {formatCurrency(stat.onramp_fees)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-orange-600">
                        {formatCurrency(stat.offramp_fees)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-vetme-gray-dark">
                        {stat.transaction_count}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-vetme-gray-dark">
                    No commission data available for the selected period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Commission;
